import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { getClassRequest } from "services/userServices";
import classes from "./ClassRequestDrawer.module.css";
import Grid from "@mui/material/Grid";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { classRequestActions } from "Store/ClassRequest";
import { Tooltip, IconButton } from "@mui/material";

const ClassRequestDrawer = ({
  classRequestId,
  open,
  onClose,
  classRosterId,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation(["classRequestDrawer"]);
  const [classRequestDetail, setClassRequestDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedProject, setExpandedProject] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        if (classRequestId) {
          const response = await getClassRequest(classRequestId);

          if (cancelled) return;

          if (response?.status && response?.data?.classRequestDetail) {
            if (!cancelled) {
              setClassRequestDetail(response.data.classRequestDetail);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching class request details:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [classRequestId]);

  const handleEditClick = () => {
    const parentClientCodeMatch =
      classRequestDetail?.parentClient?.match(/\(([^)]+)\)$/);
    const parentClientCode = parentClientCodeMatch
      ? parentClientCodeMatch[1]
      : "";

    const transformClassRequestData = (data) => {
      const projectArray = data.project
        ? data.project
            .split(",")
            .map((project) => {
              const matches = project.trim().match(/^(.+)\s*\(([^)]+)\)$/);
              if (!matches) return null;
              const [_, name, code] = matches;
              return {
                projectName: name.trim(),
                projectCode: code.trim(),
                fullName: project.trim(),
                selected: true,
              };
            })
            .filter(Boolean)
        : [];

      return {
        classRequestId: data.classRequestId ?? null,
        title: data.title ?? null,
        requestStatus: data.requestStatus ?? null,
        parentClient: data.parentClient ?? "",
        client: data.client ?? "",
        program: data.program ?? "",
        project: projectArray,
        selectedProject: projectArray,
        projectList: projectArray,
        siteLocation: data.siteLocation ?? "",
        numberOfClasses: data.numberOfClasses?.toString() ?? "0",
        classRosterCount: data.classRosterCount ?? 0,
        wave: data.wave ? data.wave : "",
        taleoRequisitions: data.taleoRequisitions ?? null,
        classType: data.classType ?? "",
        trainingProduction: data.trainingProduction ?? "",
        classFillGoal: data.classFillGoal?.toString() ?? "0",

        productionFillGoalBillable: data.productionFillGoalBillable ?? 0,
        productionFillGoalNonBillable: data.productionFillGoalNonBillable ?? 0,
        trainingClassification: data.trainingClassification ?? "",
        productionClassification: data.productionClassification ?? "",
        hiringBands: data.hiringBands ?? "",
        rosterLockDate: data.rosterLockDate ?? "",
        gpmpoc: data.gpmpoc ?? "",
        tpcHosts: data.tpcHosts ?? "",
        createdDate: data.createdDate ?? "",
        createdBy: data.createdBy ?? "",
        siteCode: data.siteCode ?? "",
        projectCode: data.projectCode ?? "",
        modifiedDate: data.modifiedDate ?? null,
        modifiedBy: data.modifiedBy ?? null,
        onboardingSupportedById: data.onboardingSupportedById ?? "",
        classApprovalDate: data.classApprovalDate ?? "",
        classApprovalComments: data.classApprovalComments ?? "",
        opsApprovalDate: data.opsApprovalDate ?? "",
        opsApproverOracleId: data.opsApproverOracleId ?? "",
      };
    };
    const transformedData = transformClassRequestData(classRequestDetail);

    dispatch(classRequestActions.addClassRequest(transformedData));

    navigate("/home/createclassrequest", {
      state: {
        from: "/home/searchrosters",
        classRequestId: transformedData.classRequestId,
        classRosterId: classRosterId,
        mode: "update",
        parentClientCode: parentClientCode,
      },
    });
  };

  const getTotalProdFillGoal = (billable, nonBillable) => {
    const billableNum = Number(billable) || 0;
    const nonBillableNum = Number(nonBillable) || 0;
    return billableNum + nonBillableNum;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          color: "#3299FF",
          fontSize: "24px",
          marginBottom: "1rem",
          fontWeight: "bold",
          fontFamily: "Urbanist",
        }}
      >
        {t("Class Request Details")}
        <Button
          variant="contained"
          size="small"
          sx={{
            backgroundColor: "#3299FF",
            width: "40px",
            height: "30px",
            marginLeft: "38rem",
            textTransform: "none",
            cursor: "pointer",
            marginTop: "-5px",
            fontSize: "14px",
          }}
          onClick={handleEditClick}
        >
          {t("Edit")}
        </Button>
        <CloseIcon
          sx={{
            fontSize: "23px",
            display: "flex",
            float: "right",
            cursor: "pointer",
            marginTop: "7px",
            color: "#2b3a42",
          }}
          onClick={onClose}
        ></CloseIcon>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className={classes.loader}></div>
        ) : classRequestDetail ? (
          <Grid className={classes.container} container>
            <Grid className={classes.keyData} item xs={4}>
              {t("Last Modified")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.modifiedBy} (
              {moment
                .utc(classRequestDetail.modifiedDate)
                .format("YYYY-MM-DD hh:mm:ss A")}
              )
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Request Status")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.requestStatus}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Project")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {(() => {
                let projects = [];
                if (typeof classRequestDetail.project === "string") {
                  projects = classRequestDetail.project
                    .split(",")
                    .map((p) => p.trim());
                } else if (Array.isArray(classRequestDetail.project)) {
                  projects = classRequestDetail.project.map((proj) =>
                    typeof proj === "object" ? proj.fullName : proj,
                  );
                } else if (classRequestDetail.project) {
                  projects = [classRequestDetail.project];
                }
                const firstProject = projects[0];
                const hiddenCount = projects.slice(1);
                return (
                  <>
                    <div>{firstProject}</div>
                    {expandedProject ? (
                      <>
                        {hiddenCount.map((proj, index) => (
                          <div key={index + 1}>{proj}</div>
                        ))}
                        <Tooltip
                          title="Click to Collapse"
                          placement="right"
                          arrow
                        >
                          <IconButton
                            sx={{
                              cursor: "pointer",
                              color: "#3299FF",
                              background: "none",
                              border: "none",
                              padding: 0,
                              font: "inherit",
                            }}
                            onClick={() => setExpandedProject(false)}
                          >
                            All Projects
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : hiddenCount.length > 0 ? (
                      <Tooltip title="Click to Expand" placement="right" arrow>
                        <IconButton
                          sx={{
                            cursor: "pointer",
                            color: "#3299FF",
                            background: "none",
                            border: "none",
                            padding: 0,
                            font: "inherit",
                          }}
                          onClick={() => setExpandedProject(true)}
                        >
                          +{hiddenCount.length}
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </>
                );
              })()}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Site/Location")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.siteLocation} ({classRequestDetail.siteCode})
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Wave")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.wave}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("ClassRequestId")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.classRequestId}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("# of Classes")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.numberOfClasses}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Class Type")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.classType}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Training/Production Solution")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.trainingProduction}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Class Fill Goal(TA)")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.classFillGoal}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Prod Fill Goal(GWFM)")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {getTotalProdFillGoal(
                classRequestDetail.productionFillGoalBillable,
                classRequestDetail.productionFillGoalNonBillable,
              )}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Training Classification")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.trainingClassification}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Production Classification")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.productionClassification}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Hiring Bands")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.hiringBands}
            </Grid>
            <Grid className={classes.keyData} item xs={4}>
              {t("Roster Lock Date (TA)")} :
            </Grid>
            <Grid className={classes.valueData} item xs={8}>
              {classRequestDetail.rosterLockDate
                ? moment
                    .utc(classRequestDetail.rosterLockDate)
                    .format("YYYY-MM-DD")
                : null}
            </Grid>
          </Grid>
        ) : (
          <div>No Data Available</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

ClassRequestDrawer.propTypes = {
  classRequestId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  classRosterId: PropTypes.string.isRequired,
};

export default ClassRequestDrawer;
